import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Search, CheckCircle2, Lock, Edit3, Zap } from 'lucide-react';

export default function AdminPayloadBuilder() {
  const [cases, setCases] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCases, setLoadingCases] = useState(true);
  const [payloadJson, setPayloadJson] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCases();
    api.get('/admin/automation-profiles').then(r => setProfiles(r.data.profiles || [])).catch(() => {});
  }, []);

  const loadCases = async () => {
    setLoadingCases(true);
    try {
      const { data } = await api.get('/admin/cases?page_size=50');
      setCases(data.cases || []);
    } catch (err) {
      toast.error('Failed to load cases');
    } finally {
      setLoadingCases(false);
    }
  };

  const loadPayload = async (caseId) => {
    setSelectedCase(caseId);
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cases/${caseId}/automation-payload`);
      setPayload(data);
      setPayloadJson(JSON.stringify(data.payload_json || {}, null, 2));
    } catch (err) {
      setPayload(null);
      setPayloadJson('');
    } finally {
      setLoading(false);
    }
  };

  const generatePayload = async () => {
    if (!selectedCase) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/cases/${selectedCase}/generate-payload`);
      setPayload(data);
      setPayloadJson(JSON.stringify(data.payload_json || {}, null, 2));
      toast.success('Payload generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate payload');
    } finally {
      setLoading(false);
    }
  };

  const savePayloadJson = async () => {
    setSaving(true);
    try {
      let parsed;
      try { parsed = JSON.parse(payloadJson); } catch (e) { toast.error('Invalid JSON'); setSaving(false); return; }
      const { data } = await api.patch(`/admin/cases/${selectedCase}/automation-payload`, { payload_json: parsed });
      setPayload(data);
      toast.success('Payload updated');
    } catch (err) {
      toast.error('Failed to update payload');
    } finally {
      setSaving(false);
    }
  };

  const approvePayload = async () => {
    try {
      const { data } = await api.post(`/admin/cases/${selectedCase}/approve-payload`);
      setPayload(data);
      toast.success('Payload approved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const lockPayload = async () => {
    try {
      const { data } = await api.post(`/admin/cases/${selectedCase}/lock-payload`);
      setPayload(data);
      toast.success('Payload locked — ready for Playwright');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to lock');
    }
  };

  const statusColor = (s) => ({
    missing_fields: 'bg-red-500/20 text-red-400',
    draft: 'bg-yellow-500/20 text-yellow-400',
    approved: 'bg-blue-500/20 text-blue-400',
    locked: 'bg-green-500/20 text-green-400',
  }[s] || 'bg-gray-500/20 text-gray-400');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Payload Builder</h2>
          <p className="text-[#8696a0] text-sm mt-1">Generate, edit, approve, and lock case payloads for Playwright automation</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadCases} className="p-2 text-[#8696a0] hover:text-white transition"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-[#111b21] rounded-xl border border-[#2a3942] p-4">
          <h3 className="text-white font-semibold text-sm mb-3">Select Case</h3>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-[#8696a0]" />
            <input
              className="w-full bg-[#202c33] text-white border border-[#374045] rounded-lg pl-9 pr-3 py-2 text-sm"
              placeholder="Search case number..."
              value={selectedCase}
              onChange={e => {
                setSelectedCase(e.target.value);
                if (e.target.value.length === 36) loadPayload(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {loadingCases ? (
              <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#00a884] mx-auto" /></div>
            ) : cases.length === 0 ? (
              <div className="text-center py-4 text-[#8696a0] text-xs">No cases found</div>
            ) : cases.map(c => (
              <button key={c.id} onClick={() => loadPayload(c.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                  selectedCase === c.id ? 'bg-[#00a884]/20 text-white' : 'text-[#8696a0] hover:bg-[#202c33]'
                }`}>
                <div className="font-medium">{c.case_number || c.id.slice(0, 8)}</div>
                <div className="text-xs opacity-70">{c.client_name || '—'} · {c.service_type || '—'}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {!selectedCase ? (
            <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-8 text-center text-[#8696a0]">
              Select a case from the left to view or generate a payload
            </div>
          ) : loading ? (
            <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-8 text-center">
              <Loader2 className="w-5 h-5 animate-spin text-[#00a884] mx-auto" />
            </div>
          ) : (
            <>
              <div className="bg-[#111b21] rounded-xl border border-[#2a3942] p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-white font-semibold">
                      Payload — {selectedCase.slice(0, 8)}
                    </h3>
                    {payload && (
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs ${statusColor(payload.payload_status)}`}>
                        {payload.payload_status || 'unknown'}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {(!payload || payload.payload_status === 'missing_fields') && (
                      <button onClick={generatePayload}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#00a884] text-white text-xs rounded-lg hover:bg-[#06cf9c]">
                        <Zap className="w-3 h-3" /> Generate Payload
                      </button>
                    )}
                  </div>
                </div>

                {payload && (
                  <>
                    {payload.missing_fields && payload.missing_fields.length > 0 && (
                      <div className="mb-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-red-400 text-xs font-medium">Missing fields ({payload.missing_fields.length})</p>
                        <p className="text-[#8696a0] text-xs mt-1">{payload.missing_fields.join(', ')}</p>
                      </div>
                    )}

                    <label className="text-[#8696a0] text-xs block mb-1">Payload JSON</label>
                    <textarea value={payloadJson} onChange={e => setPayloadJson(e.target.value)}
                      className="w-full bg-[#202c33] text-white border border-[#374045] rounded-lg px-3 py-2 text-xs font-mono h-64 resize-y"
                      disabled={payload.payload_status === 'locked'} />

                    <div className="flex gap-2 mt-3 flex-wrap">
                      {payload.payload_status !== 'locked' && (
                        <button onClick={savePayloadJson} disabled={saving}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#202c33] text-white text-xs rounded-lg hover:bg-[#2a3942] disabled:opacity-50">
                          <Edit3 className="w-3 h-3" /> {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      )}
                      {payload.payload_status === 'draft' && (
                        <button onClick={approvePayload}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">
                          <CheckCircle2 className="w-3 h-3" /> Approve Payload
                        </button>
                      )}
                      {payload.payload_status === 'approved' && (
                        <button onClick={lockPayload}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">
                          <Lock className="w-3 h-3" /> Lock Payload
                        </button>
                      )}
                      {payload.payload_status === 'locked' && (
                        <span className="text-green-400 text-xs flex items-center gap-1 px-3 py-1.5">
                          <Lock className="w-3 h-3" /> Locked — ready for Playwright execution
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
